import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Stack, Text, Icon, IconButton, Shimmer, ShimmerElementType, MessageBar, MessageBarType, TooltipHost } from "@fluentui/react";
import { Sparkline } from "@fluentui/react-charting";
import { CUSTOMER_HEALTH_STATUS_COLORS, MTPCustomerHealthTooltips, resolveIndicatorTooltip } from "./CustomerHealthView.constants";
import {
  useCustomerHealthData,
  useCustomerHealthProductTrendData,
  HealthViewMode
} from "./useCustomerHealthHooks";
import MTPDeploymentRawIndicatorCards from "./MTPDeploymentRawIndicatorCards";
import MTPEngagementRawIndicatorCards from "./MTPEngagementRawIndicatorCards";
import MTPCommunityEngagementRawIndicatorCards from "./MTPCommunityEngagementRawIndicatorCards";
import MTPChurnRawIndicatorCards from "./MTPChurnRawIndicatorCards";
import MTPMaturityHealthScoreRawIndicatorCards from "./MTPMaturityHealthScoreRawIndicatorCards";
import SHIScoreRawIndicatorCards from "./SHIScoreRawIndicatorCards";
import FeatureHealthRawIndicatorCards from "./FeatureHealthRawIndicatorCards";

interface MTPProductDetailsProps {
  tenantId: string;
  viewMode?: HealthViewMode;
  hideDetails?: boolean;
}

export interface MTPProductDetailsHandle {
  selectCell: (product: string, indicator: string) => void;
}

interface IndicatorMetric {
  label: string;
  value: number | string;
}

interface IndicatorCard {
  title: string;
  metrics: IndicatorMetric[];
}

const formatPascalCase = (text: string): string => {
  if (text === "ChurnRate4Months") return "4 Months Churn Rate";
  return text.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
};

const getValueColor = (label: string, value: number) => {
  if (label.toLowerCase() !== "score") return undefined;
  if (value >= 71) return CUSTOMER_HEALTH_STATUS_COLORS.Healthy;
  if (value >= 41) return CUSTOMER_HEALTH_STATUS_COLORS.Warning;
  return CUSTOMER_HEALTH_STATUS_COLORS.Critical;
};

// Maps heat map short product names → product card full names
const HEATMAP_TO_PRODUCT_NAME: Record<string, string> = {
  "MDA": "Microsoft Defender for Cloud Apps",
  "MDC": "Microsoft Defender for Cloud",
  "MDE": "Microsoft Defender for Endpoint",
  "MDI": "Microsoft Defender for Identity",
  "MDO": "Microsoft Defender for Office 365",
  "Sentinel": "Microsoft Sentinel",
};

// Reverse map: full product name → abbreviated name
const PRODUCT_TO_ABBR: Record<string, string> = {
  "Microsoft Defender for Cloud Apps": "MDA",
  "Microsoft Defender for Cloud": "MDC",
  "Microsoft Defender for Endpoint": "MDE",
  "Microsoft Defender for Identity": "MDI",
  "Microsoft Defender for Office 365": "MDO",
  "Microsoft Sentinel": "Sentinel",
};

// Abbreviation → full product name (reverse of PRODUCT_TO_ABBR)
const ABBR_TO_PRODUCT: Record<string, string> = Object.fromEntries(
  Object.entries(PRODUCT_TO_ABBR).map(([name, abbr]) => [abbr, name])
);

const DEPLOYMENT_PRODUCT_ORDER  = ["MDO", "MDE", "MDI", "MDA", "MDC", "Sentinel"] as const;
const ENGAGEMENT_PRODUCT_ORDER   = ["MDO", "MDE", "MDI", "MDA", "Sentinel"] as const;
const COMMUNITY_ENGAGEMENT_PRODUCT_ORDER = ["MDO", "MDE", "MDI", "MDA", "MDC", "Sentinel"] as const;
const CHURN_PRODUCT_ORDER        = ["MDE", "MDO", "MDI", "MDA", "MDC"] as const;

const MTPProductDetails = React.forwardRef<MTPProductDetailsHandle, MTPProductDetailsProps>(({ tenantId, viewMode = "tenant", hideDetails = false }, ref) => {
  const entityLabel = viewMode === "tpid" ? "TPID" : "tenant";
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedIndicator, setSelectedIndicator] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const indicatorScrollRef = useRef<HTMLDivElement>(null);
  const [isIndicatorOverflowing, setIsIndicatorOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    selectCell: (product: string, indicator: string) => {
      const mappedProduct = HEATMAP_TO_PRODUCT_NAME[product] ?? product;
      setSelectedProduct(mappedProduct);
      setSelectedIndicator(indicator);
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (scrollContainerRef.current) {
          const productCard = scrollContainerRef.current.querySelector(
            `[data-product="${CSS.escape(mappedProduct)}"]`
          ) as HTMLElement | null;
          productCard?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
      }, 100);
      setTimeout(() => {
        if (indicatorScrollRef.current) {
          const indicatorCard = indicatorScrollRef.current.querySelector(
            `[data-indicator="${CSS.escape(indicator)}"]`
          ) as HTMLElement | null;
          indicatorCard?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
      }, 300);
    }
  }));

  const checkOverflow = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
    const indEl = indicatorScrollRef.current;
    if (indEl) {
      setIsIndicatorOverflowing(indEl.scrollWidth > indEl.clientWidth);
    }
  }, []);

  const scrollBy = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const amount = direction === "left" ? -240 : 240;
      scrollContainerRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  const scrollIndicatorsBy = (direction: "left" | "right") => {
    if (indicatorScrollRef.current) {
      const amount = direction === "left" ? -280 : 280;
      indicatorScrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  const { data: trendData, isLoading: trendLoading, isError: trendError } =
    useCustomerHealthProductTrendData(tenantId, "MTP", 6, viewMode);

  const { data: healthResponse, isLoading: healthLoading, isError: healthError } =
    useCustomerHealthData(tenantId, "MTP", viewMode);

  const healthData = healthResponse?.customerHealthDataList ?? healthResponse;

  const products = useMemo(() => {
    if (!trendData || !healthData) return [];

    // Score from healthData where subCategory1 === "CustomerHealthScore"
    const scoreMap: Record<string, number> = {};
    for (const item of healthData) {
      if (
        item.category === "Indicator" &&
        item.subCategory1 === "CustomerHealthScore" &&
        item.productName !== "Overall" &&
        !(item.productName in scoreMap)
      ) {
        scoreMap[item.productName] = Number(item.value);
      }
    }

    const dateMap: Record<string, { date: string; score: number }[]> = {};
    const trendSeen = new Set<string>();
    for (const item of trendData) {
      const name = item.productName;
      if (name === "Overall") continue;
      const key = `${name}|${item.factDate}`;
      if (trendSeen.has(key)) continue;
      trendSeen.add(key);
      if (!dateMap[name]) dateMap[name] = [];
      dateMap[name].push({ date: item.factDate, score: Number(item.customerHealthScore) });
    }

    return Object.entries(dateMap).map(([name, entries]) => {
      const sorted = [...entries].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const trendScores = sorted.map((e) => e.score);
      const score = scoreMap[name] ?? trendScores[trendScores.length - 1];

      let scoreColor: string;
      if (score >= 71) scoreColor = CUSTOMER_HEALTH_STATUS_COLORS.Healthy;
      else if (score >= 41) scoreColor = CUSTOMER_HEALTH_STATUS_COLORS.Warning;
      else scoreColor = CUSTOMER_HEALTH_STATUS_COLORS.Critical;

      const latest = trendScores[trendScores.length - 1];
      const previous = trendScores.length >= 2 ? trendScores[trendScores.length - 2] : latest;
      let sparklineColor: string;
      if (latest < previous) sparklineColor = CUSTOMER_HEALTH_STATUS_COLORS.Critical;
      else if (latest > previous) sparklineColor = CUSTOMER_HEALTH_STATUS_COLORS.Healthy;
      else sparklineColor = CUSTOMER_HEALTH_STATUS_COLORS.Warning;

      const delta = latest - previous;
      return { name, score, trend: trendScores, scoreColor, sparklineColor, delta };
    });
  }, [trendData, healthData]);

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [checkOverflow, products]);

  useEffect(() => {
    if (products.length > 0 && !selectedProduct) {
      setSelectedProduct(products[0].name);
    }
  }, [products]);

  const indicators = useMemo((): IndicatorCard[] => {
    if (!healthData || !selectedProduct) return [];

    // Filter: matching product, category=Indicator, exclude CustomerHealthScore
    const filtered = healthData.filter(
      (item: any) =>
        item.productName === selectedProduct &&
        item.category === "Indicator" &&
        item.subCategory1 !== "CustomerHealthScore"
    );

    // Deduplicate by subCategory1 + subCategory2
    const seen = new Set<string>();
    const deduped = filtered.filter((item: any) => {
      const key = `${item.subCategory1}|${item.subCategory2}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Group by subCategory1 (indicator name), collect subCategory2 as metric labels
    const grouped: Record<string, IndicatorMetric[]> = {};
    for (const item of deduped) {
      const indicatorName = item.subCategory1;
      if (!indicatorName) continue;
      if (!grouped[indicatorName]) grouped[indicatorName] = [];

      let label = item.subCategory2;
      let value = item.value;

      if (label === "Weight Percentage") {
        label = "Weight";
        value = `${value}%`;
      } else if (label === "Contribution Points") {
        label = "Contribution";
        value = `${value} pts`;
      }

      grouped[indicatorName].push({ label, value });
    }

    const metricSortOrder: Record<string, number> = { "Score": 0, "Weight": 1, "Contribution": 2 };

    const indicatorSortOrder: Record<string, number> = {
      "deployment": 1,
      "product engagement": 2,
      "engagement": 2,
      "support health score": 3,
      "shi score": 3,
      "community engagement": 4,
      "churn": 5,
      "maturity health score": 6,
      "feature health score": 7,
      "feature request health": 7,
    };

    const getIndicatorOrder = (title: string): number => {
      const display = formatPascalCase(title).toLowerCase();
      for (const [key, order] of Object.entries(indicatorSortOrder)) {
        if (display.includes(key)) return order;
      }
      return 99;
    };

    return Object.entries(grouped)
      .map(([title, metrics]) => ({
        title,
        metrics: metrics.sort((a, b) => (metricSortOrder[a.label] ?? 99) - (metricSortOrder[b.label] ?? 99)),
      }))
      .sort((a, b) => getIndicatorOrder(a.title) - getIndicatorOrder(b.title));
  }, [healthData, selectedProduct]);

  useEffect(() => {
    checkOverflow();
  }, [indicators, checkOverflow]);

  useEffect(() => {
    if (indicators.length > 0 && !selectedIndicator) {
      setSelectedIndicator(indicators[0].title);
    }
  }, [indicators]);

  const rawData = useMemo((): { label: string; value: number | string }[] => {
    if (!healthData || !selectedProduct || !selectedIndicator) return [];

    const resolveRawLabel = (item: any): string => {
      const candidates = [
        item.subCategory4,
        item.subCategory2,
        item.subCategory3,
        item.subCategory1
      ];

      for (const candidate of candidates) {
        const text = String(candidate ?? "").trim();
        if (text) return text;
      }

      return "Metric";
    };

    const seen = new Set<string>();
    return healthData.filter((item: any) => {
      const indicatorInSubCategory1 = item.subCategory1 === selectedIndicator;
      const indicatorInSubCategory3 = item.subCategory3 === selectedIndicator;
      if (
        item.productName !== selectedProduct ||
        item.category !== "RawData" ||
        (!indicatorInSubCategory1 && !indicatorInSubCategory3)
      ) return false;
      const label = resolveRawLabel(item);
      const key = `${selectedIndicator}|${String(label)}|${String(item.value)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map((item: any) => {
      let value = String(item.value);
      if (item.subCategory2 === "IsEngaged") {
        value = Number(value) === 1 ? "Yes" : "No";
      } else if (value.trim() !== "" && !isNaN(Number(value))) {
        value = Number(value).toLocaleString();
      }
      return { label: resolveRawLabel(item), value };
    });
  }, [healthData, selectedProduct, selectedIndicator]);

  const rawIndicatorRows = useMemo(() => {
    if (!healthData || !selectedProduct || !selectedIndicator) return [];

    const rows = healthData
      .filter((item: any) =>
        item.productName === selectedProduct &&
        item.category === "RawData" &&
        item.subCategory1 === selectedIndicator &&
        item.subCategory2
      )
      .map((item: any) => ({
        sectionName: item.subCategory2,
        metricName:  item.subCategory3,
        factorLabel: item.subCategory4,
        value:       item.value,
      }));

    const seen = new Set<string>();
    return rows.filter((row: { sectionName: string; metricName: string; factorLabel: string; value: any }) => {
      const key = `${row.sectionName}|${row.metricName}|${row.factorLabel}|${String(row.value)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [healthData, selectedProduct, selectedIndicator]);

  const isDeploymentRawData               = selectedIndicator?.toLowerCase() === "deployment";
  const isProductEngagementRawData         = selectedIndicator?.toLowerCase() === "product engagement";
  const isCommunityEngagementRawData       = selectedIndicator?.toLowerCase() === "community engagement";
  const isChurnRawData                     = selectedIndicator?.toLowerCase() === "churn";
  const isMaturityRawData                  = selectedIndicator?.toLowerCase() === "maturity health score";
  const isSHIRawData           = /support.health|shi.score/i.test(selectedIndicator ?? "");
  const isFeatureHealthRawData = /feature.health/i.test(selectedIndicator ?? "");

  const allProductDeploymentRows = useMemo(() => {
    if (!healthData || selectedIndicator?.toLowerCase() !== "deployment") return [];

    return DEPLOYMENT_PRODUCT_ORDER
      .map((abbr) => {
        const productName = ABBR_TO_PRODUCT[abbr];
        if (!productName) return null;

        const seen = new Set<string>();
        const rows = healthData
          .filter((item: any) =>
            item.productName === productName &&
            item.category === "RawData" &&
            item.subCategory1 === "Deployment" &&
            item.subCategory2
          )
          .map((item: any) => ({
            sectionName: item.subCategory2,
            metricName:  item.subCategory3,
            factorLabel: item.subCategory4,
            value:       item.value,
          }))
          .filter((row: { sectionName: string; metricName: string; factorLabel: string; value: any }) => {
            const key = `${row.sectionName}|${row.metricName}|${row.factorLabel}|${String(row.value)}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

        return rows.length > 0 ? { abbr, rows } : null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [healthData, selectedIndicator]);

  const allProductEngagementRows = useMemo(() => {
    if (!healthData || !isProductEngagementRawData) return [];

    return ENGAGEMENT_PRODUCT_ORDER
      .map((abbr) => {
        const productName = ABBR_TO_PRODUCT[abbr];
        if (!productName) return null;

        const seen = new Set<string>();
        const rows = healthData
          .filter((item: any) =>
            item.productName === productName &&
            item.category === "RawData" &&
            item.subCategory1 === "Product Engagement" &&
            item.subCategory2
          )
          .map((item: any) => ({
            sectionName: item.subCategory2,
            metricName:  item.subCategory3,
            factorLabel: item.subCategory4,
            value:       item.value,
          }))
          .filter((row: { sectionName: string; metricName: string; factorLabel: string; value: any }) => {
            const key = `${row.sectionName}|${row.metricName}|${row.factorLabel}|${String(row.value)}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

        return rows.length > 0 ? { abbr, rows } : null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [healthData, isProductEngagementRawData]);

  // Weight for the Product Engagement indicator (e.g. "20%")
  const engagementWeight = useMemo(() => {
    const ind = indicators.find(
      (i) => i.title.toLowerCase().replace(/\s/g, "") === "productengagement"
    );
    const wMetric = ind?.metrics.find((m) => m.label.toLowerCase() === "weight");
    return wMetric ? String(wMetric.value) : undefined;
  }, [indicators]);

  const allProductCommunityEngagementRows = useMemo(() => {
    if (!healthData || !isCommunityEngagementRawData) return [];

    return COMMUNITY_ENGAGEMENT_PRODUCT_ORDER
      .map((abbr) => {
        const productName = ABBR_TO_PRODUCT[abbr];
        if (!productName) return null;

        const seen = new Set<string>();
        const rows = healthData
          .filter((item: any) =>
            item.productName === productName &&
            item.category === "RawData" &&
            item.subCategory1 === "Community Engagement" &&
            item.subCategory2
          )
          .map((item: any) => ({
            sectionName: item.subCategory2,
            metricName:  item.subCategory3,
            factorLabel: item.subCategory4,
            value:       item.value,
          }))
          .filter((row: { sectionName: string; metricName: string; factorLabel: string; value: any }) => {
            const key = `${row.sectionName}|${row.metricName}|${row.factorLabel}|${String(row.value)}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

        return rows.length > 0 ? { abbr, rows } : null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [healthData, isCommunityEngagementRawData]);

  const communityEngagementWeight = useMemo(() => {
    const ind = indicators.find(
      (i) => i.title.toLowerCase().replace(/\s/g, "") === "communityengagement"
    );
    const wMetric = ind?.metrics.find((m) => m.label.toLowerCase() === "weight");
    return wMetric ? String(wMetric.value) : undefined;
  }, [indicators]);

  const maturityRows = useMemo(() => {
    if (!healthData || !isMaturityRawData || !selectedProduct) return [];

    const seen = new Set<string>();
    return healthData
      .filter((item: any) =>
        item.productName === selectedProduct &&
        item.category === "RawData" &&
        item.subCategory1 === "Maturity Health Score" &&
        item.subCategory2
      )
      .map((item: any) => ({
        sectionName: item.subCategory2,
        metricName:  item.subCategory3,
        factorLabel: item.subCategory4,
        value:       item.value,
      }))
      .filter((row: { sectionName: string; metricName: string; factorLabel: string; value: any }) => {
        const key = `${row.sectionName}|${row.metricName}|${row.factorLabel}|${String(row.value)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [healthData, isMaturityRawData]);

  const shiRows = useMemo(() => {
    if (!healthData || !isSHIRawData || !selectedProduct) return [];

    const seen = new Set<string>();
    return healthData
      .filter((item: any) =>
        item.productName === selectedProduct &&
        item.category === "RawData" &&
        /support.health|shi.score/i.test(item.subCategory1 ?? "") &&
        item.subCategory2
      )
      .map((item: any) => ({
        sectionName: item.subCategory2,
        metricName:  item.subCategory3,
        factorLabel: item.subCategory4,
        value:       item.value,
      }))
      .filter((row: { sectionName: string; metricName: string; factorLabel: string; value: any }) => {
        const key = `${row.sectionName}|${row.metricName}|${row.factorLabel}|${String(row.value)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [healthData, isSHIRawData, selectedProduct]);

  const featureHealthRows = useMemo(() => {
    if (!healthData || !isFeatureHealthRawData || !selectedProduct) return [];

    const seen = new Set<string>();
    return healthData
      .filter((item: any) =>
        item.productName === selectedProduct &&
        item.category === "RawData" &&
        /feature.health/i.test(item.subCategory1 ?? "") &&
        item.subCategory2
      )
      .map((item: any) => ({
        sectionName: item.subCategory2,
        metricName:  item.subCategory3,
        factorLabel: item.subCategory4,
        value:       item.value,
      }))
      .filter((row: { sectionName: string; metricName: string; factorLabel: string; value: any }) => {
        const key = `${row.sectionName}|${row.metricName}|${row.factorLabel}|${String(row.value)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [healthData, isFeatureHealthRawData, selectedProduct]);

  const allProductChurnRows = useMemo(() => {
    if (!healthData || !isChurnRawData) return [];

    return CHURN_PRODUCT_ORDER
      .map((abbr) => {
        const productName = ABBR_TO_PRODUCT[abbr];
        if (!productName) return null;

        const seen = new Set<string>();
        const rows = healthData
          .filter((item: any) =>
            item.productName === productName &&
            item.category === "RawData" &&
            item.subCategory1 === "Churn" &&
            item.subCategory2
          )
          .map((item: any) => ({
            sectionName: item.subCategory2,
            metricName:  item.subCategory3,
            factorLabel: item.subCategory4,
            value:       item.value,
          }))
          .filter((row: { sectionName: string; metricName: string; factorLabel: string; value: any }) => {
            const key = `${row.sectionName}|${row.metricName}|${row.factorLabel}|${String(row.value)}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

        return rows.length > 0 ? { abbr, rows } : null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [healthData, isChurnRawData]);

  if (trendLoading || healthLoading) {
    return (
      <Stack tokens={{ childrenGap: 32 }}>
        {/* Product Cards shimmer */}
        <Stack tokens={{ childrenGap: 12 }}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 200, height: 18 }]} />
          <Stack horizontal tokens={{ childrenGap: 12 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Stack key={i} tokens={{ childrenGap: 8 }} styles={{ root: { width: 240, minWidth: 240, padding: 16, border: "1px solid #EDEBE9", borderRadius: 8 } }}>
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "80%", height: 16 }]} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 60, height: 24 }]} />
                <div style={{ height: 1, backgroundColor: "#EDEBE9", marginTop: 8, marginBottom: 4 }} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "100%", height: 30 }]} />
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Indicator Cards shimmer */}
        <Stack tokens={{ childrenGap: 12 }}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 160, height: 18 }]} />
          <Stack horizontal tokens={{ childrenGap: 12 }}>
            {[0, 1, 2, 3].map((i) => (
              <Stack key={i} tokens={{ childrenGap: 12 }} styles={{ root: { flex: "1 1 0", minWidth: 180, padding: 16, border: "1px solid #EDEBE9", borderRadius: 8 } }}>
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "70%", height: 16 }]} />
                <Stack horizontal tokens={{ childrenGap: 24 }}>
                  {[0, 1, 2].map((j) => (
                    <Stack key={j} tokens={{ childrenGap: 4 }}>
                      <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 50, height: 12 }]} />
                      <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 40, height: 16 }]} />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Raw Data shimmer */}
        <Stack tokens={{ childrenGap: 12 }}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 120, height: 18 }]} />
          <Stack horizontal tokens={{ childrenGap: 12 }}>
            {[0, 1, 2, 3].map((i) => (
              <Stack key={i} tokens={{ childrenGap: 8 }} styles={{ root: { width: 200, minWidth: 200, padding: 16, border: "1px solid #EDEBE9", borderRadius: 8 } }}>
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "80%", height: 14 }]} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 60, height: 24 }]} />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  if (trendError || healthError) {
    return (
      <MessageBar messageBarType={MessageBarType.error}>
        Failed to load product data. Please refresh the page or try again later.
      </MessageBar>
    );
  }

  if (products.length === 0) {
    return (
      <MessageBar messageBarType={MessageBarType.warning}>
        No product data available for this {entityLabel}.
      </MessageBar>
    );
  }

  return (
    <div ref={containerRef}>
    <Stack tokens={{ childrenGap: 32 }}>
      {/* Product Cards */}
      <Stack tokens={{ childrenGap: 12 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
            MTP - Products
          </Text>
          <TooltipHost content="Displays health scores, 6 month trend and delta from previosu month across products from MTP family">
            <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
          </TooltipHost>
          {!hideDetails && (
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              {products.length} Products. Equal weight (Click any Product card to view indicators and raw data contributing to the score)
            </Text>
          )}
        </Stack>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { width: "100%" } }}>
          {!hideDetails && isOverflowing && (
            <IconButton
              iconProps={{ iconName: "ChevronLeft" }}
              onClick={() => scrollBy("left")}
              styles={{ root: { minWidth: 32, height: 32, flexShrink: 0 } }}
              ariaLabel="Scroll left"
            />
          )}
          <div
            ref={scrollContainerRef}
            style={{
              display: "flex",
              gap: 12,
              overflowX: hideDetails ? "hidden" : "auto",
              scrollbarWidth: "none",
              flex: 1,
              padding: "4px 0",
              flexWrap: hideDetails ? "wrap" : "nowrap",
            }}
          >
          {products.map((product) => {
            const isSelected = selectedProduct === product.name;
            return (
              <Stack
                key={product.name}
                data-product={product.name}
                tokens={{ childrenGap: 8 }}
                onClick={() => {
                  setSelectedProduct(product.name);
                  setSelectedIndicator(null);
                }}
                styles={{
                  root: {
                    padding: "16px",
                    border: isSelected ? "2px solid #0078D4" : "1px solid #EDEBE9",
                    borderLeftWidth: 3,
                    borderLeftColor: isSelected ? "#0078D4" : "#EDEBE9",
                    borderRadius: 8,
                    backgroundColor: "#FFFFFF",
                    width: hideDetails ? "calc(33.33% - 8px)" : 240,
                    minWidth: hideDetails ? 0 : 240,
                    flexShrink: 0,
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    ":hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)", borderLeftColor: "#0078D4", backgroundColor: "#F5F5F5" },
                  },
                }}
              >
                <Stack horizontal horizontalAlign="space-between" verticalAlign="start" styles={{ root: { minHeight: 40 } }}>
                  <Text variant="medium" styles={{ root: { fontWeight: 600, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: "20px" } }}>
                    {product.name}
                  </Text>
                  <TooltipHost content={MTPCustomerHealthTooltips.Products[product.name] ?? product.name}>
                    <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
                  </TooltipHost>
                </Stack>

                <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: product.scoreColor } }}>
                  {product.score}
                </Text>

                <div style={{ height: 1, backgroundColor: "#EDEBE9", marginTop: 8, marginBottom: 4 }} />

                <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{ childrenGap: 8 }}>
                  <Sparkline
                    data={{
                      chartTitle: `${product.name} Trend`,
                      lineChartData: [
                        {
                          legend: `${product.name} Score Trend`,
                          color: product.sparklineColor,
                          data: product.trend.map((value, index) => ({ x: index + 1, y: value })),
                        },
                      ],
                    }}
                    width={120}
                    height={30}
                    showLegend={false}
                  />
                  <Stack horizontalAlign="center" tokens={{ childrenGap: 2 }}>
                    <Text
                      variant="mediumPlus"
                      styles={{
                        root: {
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          color: product.delta > 0
                            ? CUSTOMER_HEALTH_STATUS_COLORS.Healthy
                            : product.delta < 0
                              ? CUSTOMER_HEALTH_STATUS_COLORS.Critical
                              : CUSTOMER_HEALTH_STATUS_COLORS.Warning,
                        },
                      }}
                    >
                      {product.delta > 0
                        ? `▲ +${product.delta} pts`
                        : product.delta < 0
                          ? `▼ ${product.delta} pts`
                          : "No change"}
                    </Text>
                    <Text variant="tiny" styles={{ root: { color: "#605E5C" } }}>vs prev. month</Text>
                  </Stack>
                </Stack>
              </Stack>
            );
          })}
          </div>
          {!hideDetails && isOverflowing && (
            <IconButton
              iconProps={{ iconName: "ChevronRight" }}
              onClick={() => scrollBy("right")}
              styles={{ root: { minWidth: 32, height: 32, flexShrink: 0 } }}
              ariaLabel="Scroll right"
            />
          )}
        </Stack>
      </Stack>

      {/* Indicator Cards */}
      {!hideDetails && selectedProduct && indicators.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Indicators
            </Text>
            <TooltipHost content={MTPCustomerHealthTooltips.Indicators}>
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Weighted contribution to product score (Click any Indicator card to view raw data &amp; methodology)
            </Text>
          </Stack>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { width: "100%" } }}>
            {isIndicatorOverflowing && (
              <IconButton
                iconProps={{ iconName: "ChevronLeft" }}
                onClick={() => scrollIndicatorsBy("left")}
                styles={{ root: { minWidth: 32, height: 32, flexShrink: 0 } }}
                ariaLabel="Scroll indicators left"
              />
            )}
            <div
              ref={indicatorScrollRef}
              style={{
                display: "flex",
                gap: 12,
                overflowX: isIndicatorOverflowing ? "auto" : "hidden",
                scrollbarWidth: "none",
                flex: 1,
                padding: "4px 0",
                flexWrap: "nowrap",
              }}
            >
              {indicators.map((indicator) => (
                <Stack
                  key={indicator.title}
                  data-indicator={indicator.title}
                  tokens={{ childrenGap: 12 }}
                  onClick={() => setSelectedIndicator(indicator.title)}
                  styles={{
                    root: {
                      padding: "16px",
                      border: selectedIndicator === indicator.title ? "2px solid #0078D4" : "1px solid #EDEBE9",
                      borderLeftWidth: 3,
                      borderLeftColor: selectedIndicator === indicator.title ? "#0078D4" : "#EDEBE9",
                      borderRadius: 8,
                      backgroundColor: "#FFFFFF",
                      width: 260,
                      minWidth: 260,
                      flexShrink: 0,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      ":hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)", borderLeftColor: "#0078D4", backgroundColor: "#F5F5F5" },
                    },
                  }}
                >
                  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { minHeight: 40 } }}>
                    <Text variant="medium" styles={{ root: { fontWeight: 600, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: "20px" } }}>
                      {formatPascalCase(indicator.title)}
                    </Text>
                    <TooltipHost content={resolveIndicatorTooltip(indicator.title, MTPCustomerHealthTooltips.IndicatorDescriptions) ?? formatPascalCase(indicator.title)}>
                      <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
                    </TooltipHost>
                  </Stack>
                  <Stack horizontal tokens={{ childrenGap: 24 }}>
                    {indicator.metrics.map((metric, idx) => {
                      const numVal = Number(metric.value);
                      const color = getValueColor(metric.label, numVal);
                      return (
                        <Stack key={`${metric.label}-${idx}`} tokens={{ childrenGap: 4 }}>
                          <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
                            {metric.label}
                          </Text>
                          <Text variant="medium" styles={{ root: { fontWeight: 600, color: color || "inherit" } }}>
                            {metric.value}
                          </Text>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
              ))}
            </div>
            {isIndicatorOverflowing && (
              <IconButton
                iconProps={{ iconName: "ChevronRight" }}
                onClick={() => scrollIndicatorsBy("right")}
                styles={{ root: { minWidth: 32, height: 32, flexShrink: 0 } }}
                ariaLabel="Scroll indicators right"
              />
            )}
          </Stack>
        </Stack>
      )}

      {/* Raw Data Cards — Deployment: one card per product, stacked vertically */}
      {!hideDetails && viewMode !== "tpid" && selectedIndicator && isDeploymentRawData && allProductDeploymentRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Raw Data
            </Text>
            <TooltipHost content="Detailed breakdown of the Deployment indicator across all MTP products">
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Deployment indicator breakdown
            </Text>
          </Stack>
          <Stack tokens={{ childrenGap: 20 }}>
            {(() => {
              const selectedAbbr = PRODUCT_TO_ABBR[selectedProduct ?? ""];
              const entry = allProductDeploymentRows.find(({ abbr }) => abbr === selectedAbbr);
              if (!entry) return null;
              return <MTPDeploymentRawIndicatorCards rows={entry.rows} productAbbr={entry.abbr} />;
            })()}
          </Stack>
        </Stack>
      )}

      {/* Raw Data Cards — Churn: simple products side-by-side, MDC full-width below */}
      {!hideDetails && viewMode !== "tpid" && selectedIndicator && isChurnRawData && allProductChurnRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Raw Data
            </Text>
            <TooltipHost content="Detailed breakdown of the Churn indicator across all MTP products">
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Churn indicator breakdown
            </Text>
          </Stack>
          <Stack tokens={{ childrenGap: 20 }}>
            {(() => {
              const selectedAbbr = PRODUCT_TO_ABBR[selectedProduct ?? ""];
              const entry = allProductChurnRows.find(({ abbr }) => abbr === selectedAbbr);
              if (!entry) return null;
              return <MTPChurnRawIndicatorCards rows={entry.rows} productAbbr={entry.abbr} />;
            })()}
          </Stack>
        </Stack>
      )}

      {/* Raw Data Cards — Community Engagement: one card per product in wrapping row */}
      {!hideDetails && viewMode !== "tpid" && selectedIndicator && isCommunityEngagementRawData && allProductCommunityEngagementRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Raw Data
            </Text>
            <TooltipHost content="Detailed breakdown of the Community Engagement indicator across all MTP products">
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Community Engagement indicator breakdown
            </Text>
          </Stack>
          {(() => {
              const selectedAbbr = PRODUCT_TO_ABBR[selectedProduct ?? ""];
              const entry = allProductCommunityEngagementRows.find(({ abbr }) => abbr === selectedAbbr);
              if (!entry) return null;
              return (
                <MTPCommunityEngagementRawIndicatorCards
                  rows={entry.rows}
                  productAbbr={entry.abbr}
                  weight={communityEngagementWeight}
                />
              );
            })()}
        </Stack>
      )}

      {/* Raw Data Cards — Product Engagement: one card per product in wrapping row */}
      {!hideDetails && viewMode !== "tpid" && selectedIndicator && isProductEngagementRawData && allProductEngagementRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Raw Data
            </Text>
            <TooltipHost content="Detailed breakdown of the Product Engagement indicator across all MTP products">
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Product Engagement indicator breakdown
            </Text>
          </Stack>
          {(() => {
              const selectedAbbr = PRODUCT_TO_ABBR[selectedProduct ?? ""];
              const entry = allProductEngagementRows.find(({ abbr }) => abbr === selectedAbbr);
              if (!entry) return null;
              return <MTPEngagementRawIndicatorCards rows={entry.rows} productAbbr={entry.abbr} weight={engagementWeight} />;
            })()}
        </Stack>
      )}

      {/* Raw Data Cards — Maturity Health Score */}
      {!hideDetails && viewMode !== "tpid" && selectedIndicator && isMaturityRawData && maturityRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Raw Data
            </Text>
            <TooltipHost content="Detailed breakdown of the Maturity Health Score indicator">
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Maturity Health Score indicator breakdown
            </Text>
          </Stack>
          <MTPMaturityHealthScoreRawIndicatorCards rows={maturityRows} />
        </Stack>
      )}

      {/* Raw Data Cards — Support Health Score */}
      {!hideDetails && viewMode !== "tpid" && selectedIndicator && isSHIRawData && shiRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Raw Data</Text>
            <TooltipHost content="Raw Data">
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Indicator level details and factors</Text>
          </Stack>
          <SHIScoreRawIndicatorCards rows={shiRows} />
        </Stack>
      )}

      {/* Raw Data Cards — Feature Health Score */}
      {!hideDetails && viewMode !== "tpid" && selectedIndicator && isFeatureHealthRawData && featureHealthRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Raw Data</Text>
            <TooltipHost content="Raw Data">
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Indicator level details and factors</Text>
          </Stack>
          <FeatureHealthRawIndicatorCards rows={featureHealthRows} />
        </Stack>
      )}

      {/* Raw Data Cards — Non-Deployment/Non-Engagement: generic cards */}
      {!hideDetails && selectedIndicator && (viewMode === "tpid" || (!isDeploymentRawData && !isProductEngagementRawData && !isCommunityEngagementRawData && !isChurnRawData && !isMaturityRawData && !isSHIRawData && !isFeatureHealthRawData)) && rawData.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Raw Data
            </Text>
            <TooltipHost content={MTPCustomerHealthTooltips.RawData}>
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Weighted contribution to product score
            </Text>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 12 }} wrap styles={{ root: { width: "100%" } }}>
            {rawData.map((item, idx) => (
              <Stack
                key={`${item.label}-${idx}`}
                verticalFill
                styles={{
                  root: {
                    padding: "16px",
                    border: "1px solid #EDEBE9",
                    borderRadius: 8,
                    backgroundColor: "#FFFFFF",
                    width: 200,
                    minWidth: 200,
                    minHeight: 100,
                    justifyContent: "space-between",
                  },
                }}
              >
                <Text variant="medium" styles={{ root: { fontWeight: 600, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: "20px", minHeight: 40 } }}>
                  {formatPascalCase(item.label)}
                </Text>
                <Text variant="xLarge" styles={{ root: { fontWeight: item.label === "IsEngaged" ? 400 : 700 } }}>
                  {item.value}
                </Text>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
    </div>
  );
});

export default MTPProductDetails;